/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PaymentPlan } from '@/types/types'
import { trackInitCheckout, trackPurchase } from '@/helpers/facebookPixelEvents'
import { PopupStatus } from '@/components/Checkout/PopupStatus'
import { setSuccessPayment } from '@/helpers/setSuccessPayment'
import LearnMore from '../PaywallUI/LearnMore'
import { checkUserSubscription } from '@/helpers/appHelpers'
import { useTheme } from '@/components/ThemeProvider'
import { PaymentFormV2 } from '@/components/Checkout/SolidGate/PaymentFormV2'

interface PaywallProps {
  paywallFile: string
  version?: string
}

const offerItems = [
  'Fun swipe-to-clean interface',
  'Smart photo suggestions',
  'Free up storage in minutes',
]

export default function PaywallOnePlan(props: PaywallProps) {
  const [paywallPlans, setPlans] = useState<PaymentPlan[]>([])
  const [activePlan, setActivePlan] = useState(0)
  const [userHasSubscription, setUserHasSubscription] = useState(false)
  const [paywallLoading, setPaywallLoading] = useState(true)
  const email = localStorage.getItem('userEmail')
  const navigate = useNavigate()
  const [successPaymentStatus, setSuccessPaymentStatus] = useState(false)
  const { theme } = useTheme()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await import(`../../data/${props.paywallFile}`)
        setPlans(response?.plans)
      } catch (error) {
        console.error('Error fetching paywall data:', error)
      }
    }
    fetchData()
    trackInitCheckout()
    setPaywallLoading(true)
    checkUserSubscription(email ?? '').then((res) => {
      setUserHasSubscription(res.user && res.user.subscriptions.length > 0)
    })
  }, [])

  useEffect(() => {
    if (successPaymentStatus) {
      trackPurchase()
    }
  }, [successPaymentStatus])

  useEffect(() => {
    localStorage.setItem('activePlan', JSON.stringify(paywallPlans[activePlan]))
  }, [paywallPlans, activePlan])

  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      window.scrollTo(0, 0)
    }, 0)
    return () => clearTimeout(scrollTimeout)
  }, [paywallPlans])

  return (
    <>
      {paywallPlans.length > 1 && (
        <>
          <section className="w-[calc(100%+2rem)] left-[-1rem] relative">
            <img src="/img/paywall-v2.png" alt="Some" />
          </section>

          <div className="absolute left-[-1rem] top-[200px] w-[calc(100%+2rem)] h-full bg-black z-[-1]"></div>

          <div className="relative">
            <section className="w-full text-white dark:text-white  rounded-4xl mt-6 mb-8   relative before:absolute before:content-[''] before:-left-px before:-right-px before:-top-px before:-bottom-px  before:bg-[#A9B2FF] dark:before:bg-transparent dark:before:bg-borgerLightGradient before:rounded-4xl">
              <div className="dark:bg-[#1A1A1D] bg-black rounded-4xl p-4 flex flex-col relative">
                <p className="text-center text-[#1D2BDB]">Special offer</p>
                <h2 className="text-LXL2 mt-2 leading-[26px] font-bold text-center">
                  Just <span className="text-[#6E6E70] line-through">$13.99</span> $4.99/Week
                </h2>
                <div className="grid mt-4 gap-2">
                  {offerItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <svg className="min-w-4 h-auto" width="14" height="13">
                        <use
                          href={`${theme === 'light' ? '/svg/sprite.svg#check-blue-new' : '/svg/sprite.svg#check-yellow-new'}`}
                        ></use>
                      </svg>
                      <p className="font-medium dark:text-white text-[#373738]">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute z-[-1] top-[-80px] left-[-1rem] w-[calc(100%+2rem)]">
                <img
                  src="/img/gradient-paywall-v2.png"
                  className="w-full"
                  width={400}
                  height={200}
                  alt="Gradient"
                />
              </div>
            </section>
          </div>

          <PaymentFormV2
            version={props?.version}
            plan={paywallPlans[activePlan]}
            email={email ?? ''}
            handleLoading={(val) => {
              if (paywallLoading) {
                setPaywallLoading(val)
                window.scrollTo(0, 0)
              }
            }}
            onSuccess={setSuccessPaymentStatus}
          />

          <section className="mt-8 mb-8">
            <h3 className="text-center mb-3 text-LXL2 leading-[26px] font-bold">
              14-day money-back guarantee
            </h3>
            <p className="text-S max-w-[343px] mx-auto leading-4 text-center text-[#C3C3C4]">
              By proceeding, you agree to try Clean Master for a special one- month price of
              nineteen dollars and ninety-nine cents. After this period, you will automatically be
              charged thirty-nine dollars and ninety-nine cents every month unless you cancel your
              subscription.
            </p>
          </section>

          <LearnMore version={props?.version} text={paywallPlans[activePlan].text} />

          {successPaymentStatus && (
            <PopupStatus
              icon="/video/scan.gif"
              title="Your payment was successfull!"
              message="Thank you!"
              buttonTitle="Proceed to my plan"
              onClick={() => setSuccessPayment({ res: true }, navigate, props.version)}
            />
          )}
          {userHasSubscription && (
            <PopupStatus
              icon="/video/scan.gif"
              title="You already have subscription"
              buttonTitle="Proceed To Download"
              onClick={() => navigate(props.version ? '/new/thank-you' : '/thank-you')}
            />
          )}
        </>
      )}
      {paywallLoading && (
        <div className="fixed top-0 left-0 w-full h-full bg-bg z-10 pointer-events-auto opacity-100">
          <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
            <svg
              viewBox="0 0 100 100"
              height="76"
              className="animate-spin brightness-0 invert-0 dark:brightness-[unset] dark:invert-[unset]"
            >
              <use href="/svg/sprite.svg#spinner"></use>
            </svg>
          </div>
        </div>
      )}
    </>
  )
}
